import { hasErrors, useNarrativeForm } from '../../hooks/useNarrativeForm';
import { validateNarrativeForm } from '../../utils';
import { useNarrativeErrorToast } from '../../hooks/useNarrativeErrorToast';
import { useNarrativeTracking } from '../../hooks/useNarrativeTracking';
import type { NarrativeForm } from '../../utils/schema';
import { useDebouncedCallback } from 'use-debounce';
import { transformFormValuesToValuation } from '../../utils/transformation';
import { useEffect, useRef } from 'react';
import type {
  FuturePEMultipleCalculationOutput,
  FuturePEMultipleFairValueCalculationOutput,
  FuturePEMultipleFairValueCalculationInput,
  FuturePEMultipleCalculationInput,
} from '../../../Narrative/components/Calculators/useFuturePEMultipleCalculation';
import { useNarrativeUpdates } from '../../hooks/useNarrativeUpdates';
import { useStartNarrativeWithSnackbar } from '../../hooks/useStartNarrative/useStartNarrative';
import { useNarrativeFormHandler } from '../../components/NarrativeFormContent';

type Props = {
  initialNarrativeForm: NarrativeForm;
  calculateFairValue: (
    values: FuturePEMultipleCalculationInput
  ) => FuturePEMultipleCalculationOutput;
  calculateRevenueGrowthRateFromFairValue: (
    values: FuturePEMultipleFairValueCalculationInput
  ) => FuturePEMultipleFairValueCalculationOutput;
  trackEvent: (data: Record<string, string | number | boolean>) => void;
  isUpdateNarrativeUpdateEnabled: boolean;
  setHasSavedContent: (hasSaved: boolean) => void;
  narrativeUpdateId?: string | null;
  narrativeId: string;
  companyId: string;
  successSnackbar: boolean;
  setNarrativeUpdateId?: (narrativeUpdateId: string) => void;
  setNarrativeId?: (narrativeId: string) => void;
  disableAutoSave?: boolean;
};

const CONTENT_VALUATION_DEBOUNCE_TIME = 1000;

export const useNarrativeFormManager = ({
  initialNarrativeForm,
  trackEvent,
  setHasSavedContent,
  narrativeUpdateId,
  narrativeId,
  successSnackbar,
  setNarrativeUpdateId,
  setNarrativeId,
  calculateFairValue,
  calculateRevenueGrowthRateFromFairValue,
  isUpdateNarrativeUpdateEnabled,
  companyId,
  disableAutoSave,
}: Props) => {
  const hasStartedNarrative = useRef(narrativeId ? true : false);

  const {
    values,
    errors,
    setSubmitCount,
    submitCount,
    validateForm,
    dirty,
    isDirty,
    resetForm,
    setPartialValues,
  } = useNarrativeForm({
    initialValues: initialNarrativeForm,
    validate: validateNarrativeForm,
    enableReinitialize: true,
  });

  useNarrativeErrorToast({ errors, submitCount });
  useNarrativeTracking({
    trackEvent: trackEvent,
    dirty,
    errors,
  });

  const {
    handleSetPartialValues,
    handleSetFairValue,
    handleContentChange,
    handleHeaderChange,
  } = useNarrativeFormHandler({
    calculateFairValue,
    calculateRevenueGrowthRateFromFairValue,
    setPartialValues,
    values,
  });

  const { handleUpdateNarrative } = useNarrativeUpdates({
    setHasSavedContent,
    includeSuccessSnackbar: successSnackbar,
  });

  const { mutate: startNarrativeAsync } = useStartNarrativeWithSnackbar({
    includeSuccessSnackbar: successSnackbar,
  });

  const debouncedHandleUpdateNarrative = useDebouncedCallback(
    handleUpdateNarrative,
    CONTENT_VALUATION_DEBOUNCE_TIME
  );

  // Should handle initial narrative creation if none exists
  useEffect(() => {
    const startNarrative = async () => {
      const response = await startNarrativeAsync({
        companyId: companyId,
      });

      if (response?.startNarrative) {
        await handleUpdateNarrative({
          contentHtml: values.content.html,
          title: values.title,
          valuation: transformFormValuesToValuation({ values }),
          rebase: true,
          narrativeUpdateId: response.startNarrative.draftUpdate?.id ?? '',
        });

        setNarrativeId?.(response.startNarrative.id ?? '');
        setNarrativeUpdateId?.(response.startNarrative.draftUpdate?.id ?? '');
      }
    };

    if (
      isUpdateNarrativeUpdateEnabled &&
      isDirty &&
      !hasStartedNarrative.current &&
      !narrativeId
    ) {
      if (hasErrors(validateForm())) {
        return;
      }
      hasStartedNarrative.current = true;

      startNarrative();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirty, isUpdateNarrativeUpdateEnabled, values]);

  // Should handle once when the narrative is created
  useEffect(() => {
    if (disableAutoSave) {
      return;
    }

    if (narrativeId) {
      hasStartedNarrative.current = true;
    }

    if (
      isUpdateNarrativeUpdateEnabled &&
      isDirty &&
      narrativeId &&
      hasStartedNarrative.current
    ) {
      if (hasErrors(validateForm())) {
        return;
      }

      debouncedHandleUpdateNarrative({
        contentHtml: values.content.html,
        title: values.title,
        valuation: transformFormValuesToValuation({ values }),
        rebase: false,
        narrativeUpdateId: narrativeUpdateId ?? '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isUpdateNarrativeUpdateEnabled,
    isDirty,
    values,
    debouncedHandleUpdateNarrative,
    validateForm,
    narrativeId,
    disableAutoSave,
    narrativeUpdateId,
  ]);

  return {
    values,
    errors,
    setSubmitCount,
    submitCount,
    validateForm,
    dirty,
    resetForm,
    handleSetPartialValues,
    handleSetFairValue,
    handleContentChange,
    handleHeaderChange,
  };
};
